import type { Place } from '../shared/contract'
import { reverseGeocode } from './api'
import { placeId, useApp } from './store'

/**
 * `/?lat=…&lon=…[&name=…]` — the link web widgets and the macOS widget open when tapped.
 * Only on the home route and only `lat`/`lon` (Studio share links use `latitude`/`longitude`
 * and must never be consumed here). Applied before the first render (main.tsx) so the linked
 * place is the first report built.
 */
export function readDeepLink(search: string): { latitude: number; longitude: number; name: string | null } | null {
  const q = new URLSearchParams(search)
  const rawLat = q.get('lat')
  const rawLon = q.get('lon')
  const lat = Number(rawLat)
  const lon = Number(rawLon)
  if (!rawLat || !rawLon || !Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
    return null
  }
  const name = (q.get('name') ?? '').trim().slice(0, 60) || null
  return { latitude: Math.round(lat * 1e4) / 1e4, longitude: Math.round(lon * 1e4) / 1e4, name }
}

const coordLabel = (lat: number, lon: number) => `${lat.toFixed(2)}, ${lon.toFixed(2)}`

/** Opens the linked place (saved + selected), then strips the link from the URL. */
export function applyDeepLink(): void {
  if (location.pathname !== '/') return
  const link = readDeepLink(location.search)
  if (!link) return
  const { addPlace, places } = useApp.getState()
  const existed = places.some((p) => p.id === placeId(link.latitude, link.longitude))
  const label = link.name ?? coordLabel(link.latitude, link.longitude)
  const provisional: Place = { latitude: link.latitude, longitude: link.longitude, name: label, region: '', label, zip: null }
  const id = addPlace(provisional)
  // Keep other parameters (e.g. ?mock=) but drop the link itself.
  const q = new URLSearchParams(location.search)
  for (const k of ['lat', 'lon', 'name']) q.delete(k)
  const rest = q.toString()
  history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`)
  // Name a newly added place once; never rename a place the user already had.
  if (existed || link.name) return
  reverseGeocode(link.latitude, link.longitude)
    .then((p) => {
      const current = useApp.getState().places.find((x) => x.id === id)
      if (current && current.label === coordLabel(link.latitude, link.longitude)) {
        useApp.getState().renamePlace(id, { name: p.name, region: p.region, label: p.label, zip: p.zip })
      }
    })
    .catch(() => {
      /* keep the coordinate label */
    })
}
